import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import MenuSection from "@/components/takeaway/MenuSection";
import DishCard from "@/components/takeaway/DishCard";
import { menuData } from "@/data/menuData";
import { menuDataEn } from "@/data/menuData.en";
import { X, Minimize2, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

interface TakeawayMenuDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TakeawayMenuDialog = ({ open, onOpenChange }: TakeawayMenuDialogProps) => {
  const { language, t } = useLanguage();
  const [isCompact, setIsCompact] = useState(true);
  
  const currentMenuData = language === 'en' ? menuDataEn : menuData;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 bg-background border-gold/20">
        <DialogHeader className="px-3 pt-3 pb-2 border-b border-border/50 bg-luxury/30">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl md:text-2xl font-serif font-bold text-gold mb-0.5">
                {t('takeaway.title')}
              </DialogTitle>
              <p className="text-[11px] text-muted-foreground">
                {t('takeaway.subtitle')}
              </p>
            </div>
            <div className="flex items-center gap-2 mr-8">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsCompact(!isCompact)}
                className="hover:bg-gold/10 hover:text-gold"
                title={isCompact ? t('takeaway.enlargeView') : t('takeaway.minimizeView')}
              >
                {isCompact ? <Maximize2 className="h-5 w-5" /> : <Minimize2 className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <ScrollArea className="h-full">
          {/* Luxury Navigation Menu */}
          <div className="sticky top-0 z-10 bg-gradient-to-b from-background/98 via-background/95 to-background/90 backdrop-blur-xl border-b border-gold/30 shadow-luxury px-6 py-2">
            <nav className="max-w-5xl mx-auto">
              <div className="flex items-center justify-center gap-6">
                {/* Indiskt */}
                <div className="flex-shrink-0 group relative">
                  <button 
                    onClick={() => document.getElementById('indiskt')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="font-serif text-gold text-xs font-bold tracking-wide hover:text-gold-light transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-gold after:to-gold-light after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 pb-0.5"
                  >
                    {t('takeaway.indian')}
                  </button>
                  <div className="absolute top-full left-0 mt-1 bg-[hsl(var(--dark-card))] backdrop-blur-xl border-2 border-gold/40 rounded-lg shadow-luxury p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 min-w-[160px]">
                    <div className="flex flex-col gap-1">
                      <button onClick={() => document.getElementById('indiska-forratter')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">{language === 'sv' ? 'Förrätter' : 'Starters'}</button>
                      <button onClick={() => document.getElementById('kockens-rekommendation')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">{language === 'sv' ? 'Rekommendation' : 'Recommendation'}</button>
                      <button onClick={() => document.getElementById('balti')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">Balti</button>
                      <button onClick={() => document.getElementById('tandoori')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">Tandoori</button>
                      <button onClick={() => document.getElementById('vegetariskt')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">{language === 'sv' ? 'Vegetariskt' : 'Vegetarian'}</button>
                      <button onClick={() => document.getElementById('naan')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">Naan</button>
                      <button onClick={() => document.getElementById('indiska-tillbehor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">{language === 'sv' ? 'Tillbehör' : 'Sides'}</button>
                    </div>
                  </div>
                </div>

                <div className="w-px h-4 bg-gold/30"></div>

                {/* Thai */}
                <div className="flex-shrink-0 group relative">
                  <button 
                    onClick={() => document.getElementById('thai')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="font-serif text-gold text-xs font-bold tracking-wide hover:text-gold-light transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-gold after:to-gold-light after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 pb-0.5"
                  >
                    {t('takeaway.thai')}
                  </button>
                  <div className="absolute top-full left-0 mt-1 bg-[hsl(var(--dark-card))] backdrop-blur-xl border-2 border-gold/40 rounded-lg shadow-luxury p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 min-w-[140px]">
                  <div className="flex flex-col gap-1">
                    <button onClick={() => document.getElementById('thai-forratter')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">{language === 'sv' ? 'Förrätter' : 'Starters'}</button>
                    <button onClick={() => document.getElementById('thai-huvudratter')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">{language === 'sv' ? 'Huvudrätter' : 'Main Courses'}</button>
                    <button onClick={() => document.getElementById('thai-tillbehor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">{language === 'sv' ? 'Tillbehör' : 'Sides'}</button>
                  </div>
                  </div>
                </div>

                <div className="w-px h-4 bg-gold/30"></div>

                {/* Sushi */}
                <div className="flex-shrink-0 group relative">
                  <button 
                    onClick={() => document.getElementById('sushi')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="font-serif text-gold text-xs font-bold tracking-wide hover:text-gold-light transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-gold after:to-gold-light after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 pb-0.5"
                  >
                    {t('takeaway.sushi')}
                  </button>
                  <div className="absolute top-full left-0 mt-1 bg-[hsl(var(--dark-card))] backdrop-blur-xl border-2 border-gold/40 rounded-lg shadow-luxury p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 min-w-[140px]">
                  <div className="flex flex-col gap-1">
                    <button onClick={() => document.getElementById('hosomaki')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">Hosomaki</button>
                    <button onClick={() => document.getElementById('uramaki')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">Uramaki</button>
                    <button onClick={() => document.getElementById('nigiri')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">Nigiri</button>
                    <button onClick={() => document.getElementById('sushi-tillbehor')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-xs text-foreground hover:text-gold transition-all duration-200 text-left px-2 py-1 rounded hover:bg-gold/10 font-medium">{language === 'sv' ? 'Tillbehör' : 'Sides'}</button>
                  </div>
                  </div>
                </div>

                <div className="w-px h-4 bg-gold/30"></div>

                {/* Barnmeny */}
                <button 
                  onClick={() => document.getElementById('barnmeny')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} 
                  className="font-serif text-gold text-xs font-bold tracking-wide hover:text-gold-light transition-all duration-300 whitespace-nowrap relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-gold after:to-gold-light after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 pb-0.5"
                >
                  {t('takeaway.barnmeny')}
                </button>
              </div>
            </nav>
          </div>

          <div className="max-w-5xl mx-auto px-3 py-3">
            {/* Indiskt Section */}
            <div id="indiskt" className="scroll-mt-20"></div>
            
            {/* Indiska Förrätter */}
            <MenuSection 
              id="indiska-forratter"
              title={t('takeaway.indiskaForratter')}
              subtitle={t('takeaway.indiskaForratter.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.indiskaForratter.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Kockens Rekommendation */}
            <MenuSection 
              id="kockens-rekommendation"
              title={t('takeaway.kockensRekommendation')}
              subtitle={t('takeaway.kockensRekommendation.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.kockensRekommendation.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Balti */}
            <MenuSection 
              id="balti"
              title={t('takeaway.balti')}
              subtitle={t('takeaway.balti.subtitle')}
              note={t('takeaway.balti.note')}
              isCompact={isCompact}
            >
              {currentMenuData.balti.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Tandoori Grillat */}
            <MenuSection 
              id="tandoori"
              title={t('takeaway.tandoori')}
              subtitle={t('takeaway.tandoori.subtitle')}
              note={t('takeaway.tandoori.note')}
              isCompact={isCompact}
            >
              {currentMenuData.tandooriGrillat.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Vegetariska */}
            <MenuSection 
              id="vegetariskt"
              title={t('takeaway.vegetariskt')}
              subtitle={t('takeaway.vegetariskt.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.vegetariskt.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Naan */}
            <MenuSection 
              id="naan"
              title={t('takeaway.naan')}
              subtitle={t('takeaway.naan.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.naan.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Indiska Tillbehör */}
            <MenuSection 
              id="indiska-tillbehor"
              title={t('takeaway.indiskaTillbehor')}
              subtitle={t('takeaway.indiskaTillbehor.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.indiskaTillbehor.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Thai Section */}
            <div id="thai" className="scroll-mt-20"></div>
            
            {/* Thai Förrätter */}
            <MenuSection 
              id="thai-forratter"
              title={t('takeaway.thaiForratter')}
              subtitle={t('takeaway.thaiForratter.subtitle')}
              note={t('takeaway.thaiForratter.note')}
              isCompact={isCompact}
            >
              {currentMenuData.thaiForratter.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Thai Huvudrätter */}
            <MenuSection 
              id="thai-huvudratter"
              title={t('takeaway.thaiHuvratter')}
              subtitle={t('takeaway.thaiHuvratter.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.thaiHuvratter.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Thailändska Tillbehör */}
            <MenuSection 
              id="thai-tillbehor"
              title={t('takeaway.thaiTillbehor')}
              subtitle={t('takeaway.thaiTillbehor.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.thaiTillbehor.map((dish, index) => (
                <DishCard key={dish.name + index} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Sushi Section */}
            <div id="sushi" className="scroll-mt-20"></div>
            
            {/* Sushi Hosomaki */}
            <MenuSection 
              id="hosomaki"
              title={t('takeaway.sushiHosomaki')}
              subtitle={t('takeaway.sushiHosomaki.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.sushiHosomaki.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Sushi Uramaki */}
            <MenuSection 
              id="uramaki"
              title={t('takeaway.sushiUramaki')}
              subtitle={t('takeaway.sushiUramaki.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.sushiUramaki.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Sushi Nigiri */}
            <MenuSection 
              id="nigiri"
              title={t('takeaway.sushiNigiri')}
              subtitle={t('takeaway.sushiNigiri.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.sushiNigiri.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Sushi Tillbehör */}
            <MenuSection 
              id="sushi-tillbehor"
              title={t('takeaway.sushiTillbehor')}
              subtitle={t('takeaway.sushiTillbehor.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.sushiTillbehor.map((dish, index) => (
                <DishCard key={dish.name + index} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>

            {/* Barnmeny */}
             <MenuSection 
              id="barnmeny"
              title={t('takeaway.barnmenySection')}
              subtitle={t('takeaway.barnmenySection.subtitle')}
              isCompact={isCompact}
            >
              {currentMenuData.barnmeny.map((dish) => (
                <DishCard key={dish.number} {...dish} isCompact={isCompact} />
              ))}
            </MenuSection>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TakeawayMenuDialog;
